'use client';
import { useCallback } from 'react';

import { useAtom, useAtomValue } from 'jotai';

import { useRouter } from 'next/navigation';

import { formStateAtom } from '@core/Atoms/Pdp/FormState.atom';
import { postcodeAtom } from '@core/Atoms/Postcode/Postcode.atom';
import { locationAtom } from '@core/Atoms/Search/LocationAtom';
import { searchParamsAtom } from '@core/Atoms/SearchParams/SearchParams.atom';
import { userIdAtom } from '@core/Atoms/User/UserId.atom';
import { Locale } from '@core/Entities/Locale/Locale.entity';
import Button from '@gds/Button/Button';
import { RichText } from '@page-builder/Components/RichText/RichText';

import { enrichPlpPath, trackClick } from '../Utils/CampaignPromo.utils';

import styles from './CampaignBlock.module.css';

interface CampaignBlockProps {
  cta: string;
  colourScheme: any;
  imageUrl: string;
  isActive: {
    codename: string;
  }[];
  locale: Locale;
  text: string;
  url: string;
  blockLoc?: string;
}

export const CampaignBlock = ({
  blockLoc,
  colourScheme,
  cta,
  imageUrl,
  isActive,
  locale,
  text,
  url,
}: CampaignBlockProps) => {
  const router = useRouter();
  const shouldShowBanner = isActive[0].codename === 'true';
  const color = colourScheme[0].codename;

  const pcAtom = useAtom(postcodeAtom);
  const location = useAtomValue(locationAtom);
  const userId = useAtomValue(userIdAtom);
  const searchParams = useAtomValue(searchParamsAtom);
  const formState = useAtomValue(formStateAtom);

  const handleClick = useCallback(() => {
    const trimUrl = url.trim();
    const plpPath = enrichPlpPath(trimUrl, pcAtom, location, locale);

    trackClick(
      {
        type: 'block',
        pathWithLocale: plpPath.split('?')[0],
        text,
        location: blockLoc,
      },
      {
        searchParams,
        userId,
        formState,
      },
    );

    router.push(plpPath);

    return;
  }, [url, pcAtom[0], pcAtom[1], locale, location, router]);

  if (!shouldShowBanner) return null;

  const ctaColor = color === 'mica_blue' ? 'white' : 'primary';

  return (
    <div
      className={styles.wrpr}
      data-color={color}
      onClick={handleClick}
      style={{ backgroundImage: `url(${imageUrl})` }}
    >
      <RichText richText={text} />
      <Button variant="outlined" color={ctaColor} onClick={handleClick}>
        {cta}
      </Button>
    </div>
  );
};

CampaignBlock.displayName = 'CampaignBlock';
